import { DetailedMessageCellRenderer } from './DetailedMessageCellRenderer';
import type { IRowNode } from '@ag-grid-community/core';
import type { Instant } from '@js-joda/core';
import { FieldTypeCategory } from '@thinkalpha/language-services';
import {
    EnumCancelRejectionReason,
    EnumOrderUpdateType,
    OrderStatus as OrderStatusSchema,
} from '@thinkalpha/trading-ws-client/contracts/order.js';
import type {
    CancelRejectionReason,
    OrderStatus,
    OrderUpdateType,
} from '@thinkalpha/trading-ws-client/contracts/order.js';
import { ColumnHeaderTooltip } from 'src/components/table-view/ColumnHeaderTooltip';
import { getOrderStatusDictionary } from 'src/lib/orders/getOrderStatusValues';
import { instrumentRenderer } from 'src/lib/renderers/instrumentRenderer';
import type { StaticGridColumn, StaticGridSettings } from 'src/types';

export enum ConnectionStatus {
    connected = 'connected',
    reconnected = 'reconnected',
    disconnected = 'disconnected',
}

export type MessagesStatus = OrderStatus | OrderUpdateType | ConnectionStatus;

export interface InternalMessage {
    type: MessagesStatus;
    id: string;
    accountId: string;
    timestamp: Instant;
    message: string;
    typeLabel: string;
}

export interface ConnectedMessage extends InternalMessage {
    type: ConnectionStatus.connected;
    message: 'Connection established';
    typeLabel: 'Connected';
}

export interface ReconnectedMessage extends InternalMessage {
    type: ConnectionStatus.reconnected;
    message: 'Connection re-established';
    typeLabel: 'Reconnected';
}

export interface DisconnectedMessage extends InternalMessage {
    type: ConnectionStatus.disconnected;
    message: 'Connection lost';
    typeLabel: 'Disconnected';
}

export interface SymbolRelatedMessage extends InternalMessage {
    symbol: string;
}

export interface OrderCreated extends SymbolRelatedMessage {
    type: 'created';
}

export interface OrderPending extends SymbolRelatedMessage {
    type: 'pending';
}

export interface OrderCanceled extends SymbolRelatedMessage {
    type: 'canceled';
    typeLabel: 'New Cancel';
}

export interface OrderAcknowledged extends SymbolRelatedMessage {
    type: 'accepted';
}

export interface OrderFilled extends SymbolRelatedMessage {
    type: 'filled';
    typeLabel: 'Order Filled';
}

export interface OrderPartiallyFilled extends SymbolRelatedMessage {
    type: 'partially-filled';
    typeLabel: 'Order Partially Filled';
}

export interface OrderUpdated extends SymbolRelatedMessage {
    type: 'updated';
    typeLabel: 'Order Updated';
}

export interface OrderSubmitted extends SymbolRelatedMessage {
    type: 'submitted';
    typeLabel: 'Order Submitted';
}

export interface OrderRejected extends SymbolRelatedMessage {
    type: 'rejected';
}

export interface OrderSuspended extends SymbolRelatedMessage {
    type: 'suspended';
}

export interface OrderCancelPending extends SymbolRelatedMessage {
    type: 'cancel-pending';
    typeLabel: 'Cancelation Pending';
}

export const CancellationRejectionDict: Record<CancelRejectionReason, string> = {
    [EnumCancelRejectionReason.BROKER_OPTION]: 'Broker option',
    [EnumCancelRejectionReason.ORDER_ALREADY_IN_PENDING_CANCEL_OR_PENDING_REPLACE_STATUS]: 'Order already pending',
    [EnumCancelRejectionReason.TOO_LATE_TO_CANCEL]: 'Too late to cancel',
    [EnumCancelRejectionReason.UNMATCHED_ACCOUNT_SYMBOL]: 'Unmatched symbol',
    [EnumCancelRejectionReason.UNKNOWN_ORDER]: 'Unknown order',
    [EnumCancelRejectionReason.MANUAL_REVIEW]: 'Manual review',
};

export interface OrderCancelRejected extends SymbolRelatedMessage {
    type: typeof EnumOrderUpdateType.cancelRejection;
    typeLabel: 'Cancel Rejected';
    message: string;
}

export type Message =
    | ConnectedMessage
    | ReconnectedMessage
    | DisconnectedMessage
    | OrderCreated
    | OrderPending
    | OrderFilled
    | OrderPartiallyFilled
    | OrderSubmitted
    | OrderCanceled
    | OrderAcknowledged
    | OrderRejected
    | OrderSuspended
    | OrderUpdated
    | OrderCancelPending
    | OrderCancelRejected;

type MessageWithAccountNameAndFirmDetails = Message & {
    accountName?: string;
    firmDetails?: { name: string; tradingSystemName: string };
};

export function isOrderStatus(status: MessagesStatus): status is OrderStatus {
    return OrderStatusSchema.safeParse(status).success;
}

export const TypeToSortRank: Record<MessagesStatus, number> = {
    // Self explanatory. We can't disconnect until after we connect.
    // We can't reconnect unless we've disconnected
    [ConnectionStatus.connected]: 1,
    [ConnectionStatus.disconnected]: 2,
    [ConnectionStatus.reconnected]: 3,

    // Self explanatory. We create an order
    // We submit it to the executor
    // Then it is pending acceptance,
    // then it's accepted -- or jumps ahead to rejected, canceled
    created: 4,
    submitted: 5,
    pending: 6,
    accepted: 7,
    updated: 7,

    // Interstitial steps that may happen, or may not
    // on our route to filling
    'partially-filled': 8,
    [EnumOrderUpdateType.cancellation]: 8,
    [EnumOrderUpdateType.correction]: 8,
    [EnumOrderUpdateType.status]: 8,

    // Try to cancel
    'cancel-pending': 9,
    'kill-pending': 9,
    'update-pending': 9,

    // Maybe cancel got rejected, it could happen.
    // After that, we get notice that something else happened to it
    [EnumOrderUpdateType.cancelRejection]: 10,

    // The actual final states possible
    filled: 11,
    rejected: 11,
    suspended: 11,
    canceled: 11,
};

// Shared between blotter and messages

export type TimestampSortValue = { timestamp: Instant; type: MessagesStatus };

export const sortByMostRecentToLeastRecent = (left: TimestampSortValue, right: TimestampSortValue) => {
    const compared = left.timestamp.compareTo(right.timestamp);
    if (compared !== 0) {
        return compared;
    }

    const leftRank = TypeToSortRank[left.type] ?? 0;
    const rightRank = TypeToSortRank[right.type] ?? 0;

    return leftRank - rightRank;
};

export const MessageGridDefaultColDefData: Record<string, Partial<StaticGridColumn>> = {
    Status: {
        hide: true,
        resizable: true,
        sortable: true,
        headerName: 'trading::messages::headers::status',
        headerTooltip: 'trading::messages::headers::status',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'status',
        field: 'Status',
    },
    Time: {
        headerName: 'trading::messages::headers::time',
        headerTooltip: 'trading::messages::headers::time',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'time',
        field: 'Time',
        resizable: true,
        sort: 'desc',
        hide: false,
        sortable: true,
        comparator: (
            leftInstant: Instant,
            rightInstant: Instant,
            nodeA: IRowNode<ProcessedRow>,
            nodeB: IRowNode<ProcessedRow>,
        ) => {
            if (!nodeA.data || !nodeB.data) {
                return leftInstant.compareTo(rightInstant);
            }

            return sortByMostRecentToLeastRecent(
                { timestamp: leftInstant, type: nodeA.data.Status },
                { timestamp: rightInstant, type: nodeB.data.Status },
            );
        },
    },
    Type: {
        headerName: 'trading::messages::headers::type',
        headerTooltip: 'trading::messages::headers::type',
        tooltipComponent: ColumnHeaderTooltip,
        field: 'Type',
        sortable: true,
        resizable: true,
        hide: false,
    },
    Symbol: {
        headerName: 'trading::messages::headers::symbol',
        headerTooltip: 'trading::messages::headers::symbol',
        tooltipComponent: ColumnHeaderTooltip,
        field: 'InstrumentSymbol',
        sortable: true,
        resizable: true,
        hide: false,
        initialWidth: 150,
        cellRenderer: instrumentRenderer,
    },
    Message: {
        headerName: 'trading::messages::headers::message',
        headerTooltip: 'trading::messages::headers::message',
        tooltipComponent: ColumnHeaderTooltip,
        field: 'Message',
        sortable: true,
        hide: false,
        resizable: true,
        cellRenderer: DetailedMessageCellRenderer,
    },
    AccountName: {
        headerName: 'trading::messages::headers::account',
        headerTooltip: 'trading::messages::headers::account',
        tooltipComponent: ColumnHeaderTooltip,
        field: 'AccountName',
        sortable: true,
        hide: false,
        resizable: true,
        initialWidth: 200,
    },
    FirmName: {
        headerName: 'trading::messages::headers::firm',
        headerTooltip: 'trading::messages::headers::firm',
        tooltipComponent: ColumnHeaderTooltip,
        field: 'FirmName',
        sortable: true,
        hide: false,
        resizable: true,
        initialWidth: 200,
    },
    FirmTradingSystemName: {
        headerName: 'trading::messages::headers::firmTradingSystemName',
        headerTooltip: 'trading::messages::headers::firmTradingSystemName',
        tooltipComponent: ColumnHeaderTooltip,
        field: 'FirmTradingSystemName',
        sortable: true,
        hide: false,
        resizable: true,
        initialWidth: 200,
    },
};

export const MessagesGridDefaults: StaticGridSettings = {
    type: 'SimpleGrid',
    columns: [
        {
            ...MessageGridDefaultColDefData.Status,
            colId: 'status',
            visible: false,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    formatting: {},
                    id: 'default__messages__status',
                    styles: {
                        text: {
                            decoration: 'underline',
                        },
                    },
                },
            ],
            id: 'Status',
            name: 'Status',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...MessageGridDefaultColDefData.Time,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    formatting: {
                        date: {
                            format: 'full',
                            isHidden: false,
                        },
                        time: {
                            format: 'full',
                            isHidden: false,
                        },
                    },
                    id: 'default__messages__timestamp',
                    styles: {},
                },
            ],
            id: 'Time',
            name: 'Time',
            dataType: FieldTypeCategory.Timestamp,
            unit: null,
        },
        {
            ...MessageGridDefaultColDefData.Type,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__messages__type',
                    formatting: {},
                    styles: {
                        text: {
                            decoration: 'underline',
                        },
                    },
                },
            ],
            id: 'Type',
            name: 'Type',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...MessageGridDefaultColDefData.Symbol,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__messages__symbol',
                    formatting: {},
                    styles: {
                        text: {
                            color: '#a08c24',
                            align: 'left',
                        },
                    },
                },
            ],

            id: 'Symbol',
            name: 'Symbol',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...MessageGridDefaultColDefData.AccountName,
            name: 'AccountName',
            visible: true,
            displaySettings: [
                { condition: { type: 'noop' }, id: 'default__messages__accountName', formatting: {}, styles: {} },
            ],
            id: 'AccountName',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...MessageGridDefaultColDefData.FirmName,
            name: 'FirmName',
            visible: true,
            displaySettings: [
                { condition: { type: 'noop' }, id: 'default__messages__firmName', formatting: {}, styles: {} },
            ],
            id: 'FirmName',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...MessageGridDefaultColDefData.FirmTradingSystemName,
            name: 'FirmTradingSystemName',
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__messages__firmTradingSystemName',
                    formatting: {},
                    styles: {},
                },
            ],
            id: 'FirmTradingSystemName',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...MessageGridDefaultColDefData.Message,
            name: 'Message',
            visible: true,
            displaySettings: [
                { condition: { type: 'noop' }, id: 'default__messages__message', formatting: {}, styles: {} },
            ],
            id: 'Message',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
    ],
    gridDisplaySettings: [
        {
            condition: {
                type: 'noop',
            },
            formatting: {
                number: {
                    locale: 'system',
                    decimalAmount: {
                        max: 2,
                        min: 2,
                    },
                },
            },
            styles: {},
            id: 'messages__grid__default',
        },
    ],
    id: 'messages__display',
    rowBasedDisplaySettings: [
        {
            id: 'status__pending',
            condition: {
                type: 'matchesValue',
                originatingFieldId: 'Status',
                matchesValue: getOrderStatusDictionary().pending,
            },
            styles: {
                text: {
                    color: 'var(--color-honey-300)',
                },
            },
            formatting: {},
        },
        {
            id: 'status__filled',
            condition: {
                type: 'matchesValue',
                originatingFieldId: 'Status',
                matchesValue: getOrderStatusDictionary().filled,
            },
            styles: {
                text: {
                    color: '#00B0F0',
                },
            },
            formatting: {},
        },
        {
            id: 'status__partially__filled',
            condition: {
                type: 'matchesValue',
                originatingFieldId: 'Status',
                matchesValue: getOrderStatusDictionary()['partially-filled'],
            },
            styles: {
                text: {
                    color: 'var(--color-honey-300)',
                },
            },
            formatting: {},
        },

        {
            id: 'status__canceled',
            condition: {
                type: 'matchesValue',
                originatingFieldId: 'Status',
                matchesValue: getOrderStatusDictionary().canceled,
            },
            styles: {
                text: {
                    color: 'var(--color-red-500)',
                },
            },
            formatting: {},
        },
        {
            id: 'status__rejected',
            condition: {
                type: 'matchesValue',
                originatingFieldId: 'Status',
                matchesValue: getOrderStatusDictionary().rejected,
            },
            styles: {
                text: {
                    color: 'var(--color-red-500)',
                },
            },
            formatting: {},
        },
        {
            id: 'status__canceled__rejected',
            condition: {
                type: 'matchesValue',
                originatingFieldId: 'Status',
                matchesValue: EnumOrderUpdateType.cancelRejection,
            },
            styles: {
                text: {
                    color: 'var(--color-red-500)',
                },
            },
            formatting: {},
        },
        {
            id: 'status__suspended',
            condition: {
                type: 'matchesValue',
                originatingFieldId: 'Status',
                matchesValue: getOrderStatusDictionary().suspended,
            },
            styles: {
                text: {
                    color: 'var(--color-orange-500)',
                },
            },
            formatting: {},
        },
    ],
};

export const processRow = (row: MessageWithAccountNameAndFirmDetails) => {
    return {
        // MessagesColumnKey.STATUS is not visible, but has to be
        // included for conditional row formatting
        // Capitalized, because we currently depend on data?.[name] on column
        // matching for parsing. These things happen.
        RowId: row.id,
        Status: row.type,
        Time: row.timestamp,
        Type: row.typeLabel,
        Message: row.message,
        AccountName: row.accountName,
        FirmName: row.firmDetails?.name,
        FirmTradingSystemName: row.firmDetails?.tradingSystemName,
        InstrumentSymbol: 'symbol' in row ? row.symbol : undefined,
    };
};

export type ProcessedRow = ReturnType<typeof processRow>;
